import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { apiPath } from '../_utils/api-version'
import { useDynamicLanguage } from '../_hooks/useShipper'
import {
  IAutoAddingProduct,
  IComboGroup,
  IFavourites,
  IMenuProduct,
  IProduct,
  IProductModifiers,
} from '../_types'
import {
  getMiniAppCrmType,
  getMiniAppProvider,
} from '../_utils/mini-app'
import { request } from './http-client'
import { TParams } from './types'

type TGetByIdParams = {
  only_delivery: string
  only_self_pickup: string
  branch_id: string
  menu_id: string
  client_id: string
  with_discounts: string
  fields?: string
  aggregator_id: string | undefined
  order_source?: string
  delivery_type?: string
}

type TGetAllParams = {
  aggregator_id: string | undefined
  page: number
  limit: number
  search?: string
  product_types?: string
  fields?: string
  client_id?: string
  menu_id: string | undefined
  product_ids?: string
  delivery_type?: string
  order_source?: string
}

const productService = {
  getAll: (
    params: TGetAllParams,
    shipper_id?: string,
    platform_id?: string,
    useDynamicLanguage = false
  ) =>
    request.get<{ products: IProduct[]; count: number }>(
      apiPath('product', useDynamicLanguage),
      {
        params: {
          ...params,
          order_source:
            params.order_source ??
            getMiniAppCrmType(getMiniAppProvider(null)),
        },
        headers: {
          Shipper: shipper_id,
          Platform: platform_id,
        },
      }
    ),

  getById: (
    id: string,
    params: TGetByIdParams,
    shipper_id?: string,
    platform_id?: string,
    useDynamicLanguage = false
  ): Promise<AxiosResponse<IProduct, any>> =>
    request.get(`${apiPath('product', useDynamicLanguage)}/${id}`, {
      params: {
        ...params,
        order_source:
          params.order_source ??
          getMiniAppCrmType(getMiniAppProvider(null)),
      },
      headers: {
        Shipper: shipper_id,
        Platform: platform_id,
      },
    }),

  getComboById: (id: string, params: TParams, useDynamicLanguage = false) =>
    request.get<{ groups: IComboGroup[] }>(
      `${apiPath('combo', useDynamicLanguage)}/${id}`,
      { params }
    ),

  getMenuProducts: (params: TParams, useDynamicLanguage = false) =>
    request.get<{ products: IMenuProduct[] }>(
      apiPath('menu-products', useDynamicLanguage),
      {
        params,
      }
    ),

  getAutoAddProduct: (params: TParams, useDynamicLanguage = false) =>
    request.get<{
      count: number | string
      products: IAutoAddingProduct[]
    }>(apiPath('auto-add-product', useDynamicLanguage), {
      params,
    }),

  getProductFavourites: (
    params: TParams,
    shipper_id?: string,
    platform_id?: string,
    useDynamicLanguage = false
  ) =>
    request.get<IFavourites>(apiPath('product-favourites', useDynamicLanguage), {
      params,
      headers: {
        Shipper: shipper_id,
        Platform: platform_id,
      },
    }),
}

export const getProductModifier = (
  params: any,
  useDynamicLanguage = false
) =>
  request.get<{
    product_modifiers: IProductModifiers
    count: string | number
  }>(apiPath('modifier', useDynamicLanguage), { params })

export default productService

export const useProductList = ({
  params,
  props = { enabled: false },
  shipper_id,
  platform_id,
}: {
  params: TGetAllParams
  props: any
  shipper_id?: string
  platform_id?: string
}) => {
  const { useDynamicLanguage: dynamicLanguageEnabled, isShipperResolved } =
    useDynamicLanguage()

  return useQuery<{ products: IProduct[]; count: string }, Error>({
    queryKey: ['product_list', props, params, dynamicLanguageEnabled],
    queryFn: () =>
      productService
        .getAll(params, shipper_id, platform_id, dynamicLanguageEnabled)
        .then((res) => res?.data),
    ...props,
    enabled: (props.enabled ?? false) && isShipperResolved,
  })
}

export const useProductListInfinite = ({
  params,
  props = { enabled: false },
  shipper_id,
  platform_id,
}: {
  params: TGetAllParams
  props?: any
  shipper_id?: string
  platform_id?: string
}) => {
  const { useDynamicLanguage: dynamicLanguageEnabled, isShipperResolved } =
    useDynamicLanguage()

  return useInfiniteQuery<{ products: IProduct[]; count: string }, Error>({
    queryKey: ['product_list_infinite', params, props, dynamicLanguageEnabled],
    queryFn: ({ pageParam = 1 }: { pageParam: number }) =>
      productService
        .getAll(
          { ...params, page: pageParam },
          shipper_id,
          platform_id,
          dynamicLanguageEnabled
        )
        .then((res) => res?.data),

    getNextPageParam: (lastPage, allPages) => {
      const totalCount = Number(lastPage?.count || 0)
      const loadedCount = allPages.reduce(
        (acc, page) => acc + (page?.products?.length || 0),
        0
      )

      const hasMore = loadedCount < totalCount
      return hasMore ? allPages.length + 1 : undefined
    },

    ...props,
    enabled: (props?.enabled ?? false) && isShipperResolved,
  })
}
